import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, InputBase, IconButton, useMediaQuery } from "@mui/material";
import {
  Search,
  CameraAlt,
  ArrowForward,
  AccessTime,
  VerifiedUser,
  Public as PublicIcon,
  Schedule,
  Description,
  WhatsApp,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Scanner from "components/Scanner";
import PublicHeaderDark from "components/PublicHeaderDark";
import PublicFooterDark from "components/PublicFooterDark";
import RevealOnScroll from "components/RevealOnScroll";
import DarkPill from "components/DarkPill";
import DarkHeroBackdrop from "components/DarkHeroBackdrop";
import { getScanText } from "utils";
import {
  SG,
  BG,
  BODY_FONT,
  PANEL,
  TEXT,
  PURPLE,
  PURPLE_LIGHT,
  PURPLE_LIGHTER,
  MAXW,
  btnP,
  btnG,
} from "theme/publicDarkTokens";

const Pill = DarkPill;

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [liveStats, setLiveStats] = useState(null);
  const [dataReady, setDataReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/api/public/stats")
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) setLiveStats(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDataReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Supports multiple tracking numbers separated by comma, space, semicolon, or newline.
  const handleSearch = (value = inputValue) => {
    const ids = String(value)
      .split(/[\s,;]+/)
      .map((v) => getScanText(v).trim())
      .filter(Boolean);
    if (ids.length === 0) return;
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("tracking-id", id));
    navigate(`/track?${params.toString()}`);
  };

  const yearsNumber = new Date().getFullYear() - 2019;

  const stats = [
    { value: `${yearsNumber}+`, label: t("home.stats.yearsExperience") },
    { value: liveStats?.packagesDelivered || "116K+", label: t("home.stats.packagesDelivered") },
    { value: liveStats?.happyCustomers || "3,100+", label: t("home.stats.happyCustomers") },
    { value: t("home.stats.operatingDaysValue"), label: t("home.stats.operatingDays"), accent: true },
  ];

  const features = [
    {
      gradient: "linear-gradient(135deg, #059669, #10B981)",
      icon: <Box sx={{ fontFamily: SG, fontWeight: 800, fontSize: 22, color: "#fff", lineHeight: 1 }}>฿</Box>,
      title: t("home.features.affordable.title"),
      description: t("home.features.affordable.description"),
    },
    {
      gradient: "linear-gradient(135deg, #3B0764, #7C3AED)",
      icon: <VerifiedUser sx={{ fontSize: 24, color: "#fff" }} />,
      title: t("home.features.freeClaim.title"),
      description: t("home.features.freeClaim.description"),
    },
    {
      gradient: "linear-gradient(135deg, #D97706, #F59E0B)",
      icon: <Schedule sx={{ fontSize: 24, color: "#fff" }} />,
      title: t("home.features.dailyCutoff.title"),
      description: t("home.features.dailyCutoff.description"),
    },
    {
      gradient: "linear-gradient(135deg, #0369A1, #0EA5E9)",
      icon: <PublicIcon sx={{ fontSize: 24, color: "#fff" }} />,
      title: t("home.features.englishSupport.title"),
      description: t("home.features.englishSupport.description"),
    },
  ];

  const thaiCities = [t("home.routeCard.cities.nongkhai"), t("home.routeCard.cities.mukdahan")];
  const laoCities = [t("home.routeCard.cities.vientiane"), t("home.routeCard.cities.savannakhet")];

  const cityChipSx = {
    fontSize: 12,
    color: "rgba(248,245,255,.42)",
    background: "rgba(248,245,255,.05)",
    px: 1.125,
    py: 0.375,
    borderRadius: "5px",
    width: "fit-content",
  };

  const renderStatValue = (value, accent) => {
    if (accent) {
      return (
        <Box sx={{ fontFamily: SG, fontWeight: 700, fontSize: 40, color: PURPLE_LIGHT, letterSpacing: "-.045em", lineHeight: 1, mb: 1.25, pt: "5px" }}>
          {value}
        </Box>
      );
    }
    const str = String(value);
    const hasPlus = str.endsWith("+");
    return (
      <Box sx={{ fontFamily: SG, fontWeight: 700, fontSize: 50, color: TEXT, letterSpacing: "-.045em", lineHeight: 1, mb: 1.25 }}>
        {hasPlus ? str.slice(0, -1) : str}
        {hasPlus && <Box component="span" sx={{ color: PURPLE, fontSize: 36 }}>+</Box>}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        background: BG,
        fontFamily: BODY_FONT,
        overflowX: "hidden",
        // Global keyframes used by the hero route card.
        "@keyframes floatAnim": { "0%,100%": { transform: "translateY(0px)" }, "50%": { transform: "translateY(-10px)" } },
        "@keyframes dashMove": { to: { strokeDashoffset: -24 } },
      }}
    >
      <PublicHeaderDark />

      {/* ══════════ HERO ══════════ */}
      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          background: BG,
          display: "flex",
          alignItems: "center",
        }}
      >
        <DarkHeroBackdrop />

        <Box
          sx={{
            position: "relative",
            maxWidth: MAXW,
            mx: "auto",
            width: "100%",
            px: { xs: 2.5, md: 5 },
            py: { xs: 7, md: 10 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 6, md: 9 },
            alignItems: "center",
          }}
        >
          {/* Copy */}
          <RevealOnScroll y={28} duration={750} enabled={dataReady}>
            <Pill
              sx={{
                background: "rgba(124,58,237,.1)",
                border: "1px solid rgba(124,58,237,.28)",
                color: PURPLE_LIGHTER,
                mb: 3,
              }}
            >
              <Box component="span" sx={{ width: 6, height: 6, background: PURPLE_LIGHT, borderRadius: "50%" }} />
              {t("home.hero.badge")}
            </Pill>

            <Typography
              component="h1"
              sx={{
                fontFamily: SG,
                fontSize: "clamp(36px, 4.4vw, 60px)",
                fontWeight: 700,
                lineHeight: 1.08,
                letterSpacing: "-.036em",
                color: TEXT,
                mb: 2.75,
              }}
            >
              {t("home.hero.titleA")}
              <br />
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(130deg, #C084FC 0%, #7C3AED 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {t("home.hero.titleHighlight")}
              </Box>
              ,<br />
              {t("home.hero.titleB")}
            </Typography>

            <Typography sx={{ fontSize: 17, color: "rgba(248,245,255,.56)", lineHeight: 1.78, mb: 5, maxWidth: 430 }}>
              {t("home.heroDescription")}
            </Typography>

            {/* Tracking input */}
            <Box sx={{ position: "relative", mb: 1.75, maxWidth: 520 }}>
              <InputBase
                fullWidth
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch(e.target.value);
                }}
                placeholder={t("home.trackingPlaceholder")}
                sx={{
                  background: "rgba(248,245,255,.06)",
                  border: "1.5px solid rgba(248,245,255,.1)",
                  color: TEXT,
                  fontSize: 15,
                  borderRadius: "12px",
                  px: 2.75,
                  py: 2,
                  pr: isNonMobile ? "150px" : "104px",
                  transition: "border-color .2s, background .2s",
                  "& input::placeholder": { color: "rgba(248,245,255,.28)", opacity: 1 },
                  "&:focus-within": { borderColor: PURPLE, background: "rgba(124,58,237,.08)" },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <IconButton
                  onClick={() => setOpen(true)}
                  aria-label="scan"
                  sx={{
                    color: "rgba(248,245,255,.6)",
                    "&:hover": { color: TEXT, background: "rgba(124,58,237,.18)" },
                  }}
                >
                  <CameraAlt sx={{ fontSize: 20 }} />
                </IconButton>
                <Button
                  onClick={() => handleSearch()}
                  sx={{ ...btnP, px: 2.25, py: 1.25, fontSize: 13, borderRadius: "8px" }}
                >
                  <Search sx={{ fontSize: 16 }} />
                  {isNonMobile && t("common.search")}
                </Button>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 12, color: "rgba(248,245,255,.26)", mb: 4.5, maxWidth: 520 }}>
              {t("home.trackingHint")}
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                sx={btnP}
                onClick={() => {
                  if (inputValue.trim()) handleSearch();
                  else navigate("/track");
                }}
              >
                {t("home.advancedTracking")}
              </Button>
              <Button sx={btnG} onClick={() => navigate("/services")}>
                {t("common.learnMore")}
              </Button>
            </Box>
          </RevealOnScroll>

          {/* Route card */}
          <RevealOnScroll x={40} y={0} duration={750} delay={120} enabled={dataReady} sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                animation: "floatAnim 5s ease-in-out 1.5s infinite",
                background: "rgba(14,10,34,.92)",
                border: "1.5px solid rgba(124,58,237,.24)",
                borderRadius: "24px",
                p: 4.5,
                width: "100%",
                maxWidth: 400,
                backdropFilter: "blur(20px)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
                <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 16, color: TEXT, letterSpacing: "-.01em" }}>
                  {t("home.routeCard.title")}
                </Typography>
                <Pill
                  sx={{
                    background: "rgba(34,197,94,.1)",
                    color: "#4ADE80",
                    border: "1px solid rgba(34,197,94,.2)",
                    textTransform: "none",
                    letterSpacing: 0,
                    px: 1.5,
                    py: 0.625,
                  }}
                >
                  <Box component="span" sx={{ width: 6, height: 6, background: "#4ADE80", borderRadius: "50%" }} />
                  {t("home.routeCard.live")}
                </Pill>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ fontSize: 30, mb: 1.25, lineHeight: 1, color: "#FFD700" }}>🇹🇭</Box>
                  <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 14, color: TEXT, mb: 1 }}>
                    {t("home.routeCard.thailand")}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {thaiCities.map((c) => (
                      <Box key={c} sx={cityChipSx}>{c}</Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, pt: 4 }}>
                  <Box component="svg" width="64" height="20" viewBox="0 0 64 20">
                    <Box
                      component="line"
                      x1="0"
                      y1="10"
                      x2="50"
                      y2="10"
                      sx={{ stroke: "rgba(168,85,247,.45)", strokeWidth: 1.5, strokeDasharray: "8 4", animation: "dashMove 2.5s linear infinite" }}
                    />
                    <path d="M 50 5 L 60 10 L 50 15" fill="none" stroke="#A855F7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </Box>
                </Box>

                <Box sx={{ flex: 1, textAlign: "right" }}>
                  <Box sx={{ fontSize: 30, mb: 1.25, lineHeight: 1 , color: "#FFD700" }}>🇱🇦</Box>
                  <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 14, color: TEXT, mb: 1 }}>
                    {t("home.routeCard.laos")}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-end" }}>
                    {laoCities.map((c) => (
                      <Box key={c} sx={cityChipSx}>{c}</Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ height: "1px", background: "rgba(248,245,255,.06)", mb: 2.75 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                {[
                  { icon: <AccessTime sx={{ fontSize: 16, color: PURPLE_LIGHT }} />, title: t("home.routeCard.cutoff"), sub: t("home.routeCard.cutoffSub") },
                  { icon: <VerifiedUser sx={{ fontSize: 16, color: PURPLE_LIGHT }} />, title: t("home.routeCard.claim"), sub: t("home.routeCard.claimSub") },
                ].map((row, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.625 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        background: "rgba(124,58,237,.12)",
                        borderRadius: "9px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {row.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT, fontFamily: SG }}>{row.title}</Typography>
                      <Typography sx={{ fontSize: 12, color: "rgba(248,245,255,.36)" }}>{row.sub}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </RevealOnScroll>
        </Box>
      </Box>

      {/* ══════════ STATS ══════════ */}
      <Box
        component="section"
        sx={{ background: PANEL, borderTop: "1px solid rgba(248,245,255,.05)", borderBottom: "1px solid rgba(248,245,255,.05)" }}
      >
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 }, py: 9 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: { xs: 5, md: 0 } }}>
            {stats.map((stat, i) => (
              <RevealOnScroll
                key={i}
                y={20}
                duration={550}
                delay={i * 100}
                sx={{
                  px: { xs: 2, md: 4 },
                  textAlign: "center",
                  borderRight: { md: i < 3 ? "1px solid rgba(248,245,255,.06)" : "none" },
                }}
              >
                {renderStatValue(stat.value, stat.accent)}
                <Typography sx={{ fontSize: 14, color: "rgba(248,245,255,.4)", letterSpacing: ".02em" }}>{stat.label}</Typography>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════ FEATURES ══════════ */}
      <Box component="section" sx={{ background: "#F0EAFF", py: 13 }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 8 }}>
            <Box
              sx={{
                display: "inline-block",
                background: "rgba(124,58,237,.09)",
                color: "#6D28D9",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                px: 2,
                py: 0.75,
                borderRadius: "100px",
                border: "1px solid rgba(124,58,237,.15)",
                mb: 2.25,
              }}
            >
              {t("home.whyChoose.badge")}
            </Box>
            <Typography
              component="h2"
              sx={{ fontFamily: SG, fontSize: "clamp(28px, 3vw, 42px)", fontWeight: 700, letterSpacing: "-.028em", color: "#0A0618", mb: 2 }}
            >
              {t("home.whyChoose.title")}
            </Typography>
            <Typography sx={{ fontSize: 17, color: "rgba(10,6,24,.52)", maxWidth: 480, mx: "auto", lineHeight: 1.75 }}>
              {t("home.whyChoose.subtitle")}
            </Typography>
          </RevealOnScroll>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4,1fr)" }, gap: 2.5 }}>
            {features.map((f, i) => (
              <RevealOnScroll key={i} y={28} duration={600} delay={i * 90}>
                <Box
                  sx={{
                    height: "100%",
                    background: "#fff",
                    borderRadius: "20px",
                    p: "32px 28px",
                    border: "1.5px solid rgba(220,210,255,.2)",
                    transition: "all .32s cubic-bezier(.16,1,.3,1)",
                    "&:hover": {
                      transform: "translateY(-7px)",
                      boxShadow: "0 28px 56px rgba(45,27,105,.18)",
                      borderColor: "rgba(124,58,237,.28)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      background: f.gradient,
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.75,
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Typography sx={{ fontFamily: SG, fontSize: 17, fontWeight: 700, color: "#0A0618", mb: 1.25, letterSpacing: "-.01em" }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "rgba(10,6,24,.52)", lineHeight: 1.78 }}>{f.description}</Typography>
                </Box>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════ CALCULATOR ══════════ */}
      <Box component="section" sx={{ background: BG, py: 13 }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={32} duration={700}>
            <Box
              sx={{
                background: PANEL,
                border: "1.5px solid rgba(124,58,237,.2)",
                borderRadius: "28px",
                overflow: "hidden",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "360px 1fr" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  background: "linear-gradient(148deg, #2D1B69 0%, #4C1D95 50%, #7C3AED 100%)",
                  p: { xs: 5, md: "60px 48px" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Box sx={{ position: "absolute", width: 280, height: 280, borderRadius: "50%", border: "1px solid rgba(255,255,255,.07)", top: -110, right: -90, pointerEvents: "none" }} />
                <Box sx={{ position: "absolute", width: 220, height: 220, borderRadius: "50%", border: "1px solid rgba(255,255,255,.05)", bottom: -90, left: -70, pointerEvents: "none" }} />
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    background: "rgba(255,255,255,.12)",
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2.5,
                    position: "relative",
                  }}
                >
                  <Description sx={{ fontSize: 34, color: "rgba(255,255,255,.92)" }} />
                </Box>
                <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 22, color: "#fff", letterSpacing: "-.02em", position: "relative" }}>
                  {t("home.calculator.badge")}
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 4, md: "60px 56px" }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography
                  component="h2"
                  sx={{ fontFamily: SG, fontSize: { xs: 26, md: 30 }, fontWeight: 700, color: TEXT, letterSpacing: "-.025em", mb: 2, lineHeight: 1.2 }}
                >
                  {t("home.calculator.title")}
                </Typography>
                <Typography sx={{ fontSize: 16, color: "rgba(248,245,255,.5)", lineHeight: 1.82, mb: 5, maxWidth: 440 }}>
                  {t("home.calculator.description")}
                </Typography>
                <Box>
                  <Button sx={{ ...btnP, fontSize: 15, px: 4, py: 1.875 }} onClick={() => navigate("/calculator")}>
                    {t("home.calculator.cta")}
                    <ArrowForward sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
              </Box>
            </Box>
          </RevealOnScroll>
        </Box>
      </Box>

      {/* ══════════ CTA BANNER ══════════ */}
      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          py: 12,
          background: "linear-gradient(140deg, #1A0845 0%, #3B1181 50%, #5B21B6 100%)",
        }}
      >
        <Box sx={{ position: "absolute", width: 700, height: 700, borderRadius: "50%", border: "1px solid rgba(255,255,255,.04)", top: -350, right: -240, pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", border: "1px solid rgba(255,255,255,.03)", bottom: -250, left: -200, pointerEvents: "none" }} />
        <Box sx={{ position: "relative", maxWidth: 700, mx: "auto", px: { xs: 2.5, md: 5 }, textAlign: "center" }}>
          <RevealOnScroll y={24}>
            <Box
              sx={{
                display: "inline-block",
                background: "rgba(255,255,255,.1)",
                border: "1px solid rgba(255,255,255,.16)",
                color: "rgba(255,255,255,.82)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                px: 2,
                py: 0.75,
                borderRadius: "100px",
                mb: 3,
              }}
            >
              {t("home.cta.badge")}
            </Box>
            <Typography
              component="h2"
              sx={{ fontFamily: SG, fontSize: "clamp(28px, 3vw, 44px)", fontWeight: 700, color: "#fff", letterSpacing: "-.03em", lineHeight: 1.1, mb: 2.25 }}
            >
              {t("home.cta.title")}
            </Typography>
            <Typography sx={{ fontSize: 17, color: "rgba(255,255,255,.6)", lineHeight: 1.78, mb: 5.5 }}>
              {t("home.cta.subtitle")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.75, justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                sx={{ ...btnP, background: "#fff", color: "#3B1181", fontSize: 15, px: 4, py: 2, "&:hover": { background: "#F0EAFF", transform: "translateY(-2px)", boxShadow: "0 8px 28px rgba(0,0,0,.25)" } }}
                onClick={() =>
                  window.open(
                    `https://wa.me/8562077790708?text=${encodeURIComponent(t("contact.whatsappCta.prefill"))}`,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <WhatsApp sx={{ fontSize: 17 }} />
                {t("contact.whatsappCta.button")}
              </Button>
              <Button
                sx={{ ...btnG, borderColor: "rgba(255,255,255,.3)", fontSize: 15, px: 4, py: 2 }}
                onClick={() => navigate("/contact")}
              >
                {t("common.contactUs")}
              </Button>
            </Box>
          </RevealOnScroll>
        </Box>
      </Box>

      <PublicFooterDark />

      {/* Scanner */}
      <Scanner
        open={open}
        setOpen={setOpen}
        closeAfterResult={true}
        onDecode={(result) => {
          if (result) {
            const cleanResult = getScanText(result);
            setOpen(false);
            navigate(`/track?tracking-id=${encodeURIComponent(cleanResult)}`);
          }
        }}
      />
    </Box>
  );
};

export default HomePage;
